/***
 *
 *   APP LAYOUT
 *   Main app layout containing the navigation and header.
 *
 *   DOCS
 *   https://docs.useMapMetrics Atlas.app/MapMetrics Atlas-web/components/layout
 *
 *   PROPS
 *   children: will be passed from router > view > here (component(s), required)
 *   title: title of the view for the header (string, required)
 *
 **********/

import { Fragment, useContext, useState } from "react";
import {
  AuthContext,
  VerticalNav,
  DrawerNav,
  Header,
  User,
  useTranslation,
} from "components/lib";
import { DateRangeInput } from "components/form/input/date/date";

export function AppLayout({ title, children }) {
  // context & style
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const [selectedRange, setSelectedRange] = useState({
    start: null,
    end: null,
  });
  const nav = [
    {
      label: t("nav.dashboard"),
      icon: ({ color }) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M2.54 0H5.92C7.33 0 8.46 1.15 8.46 2.561V5.97C8.46 7.39 7.33 8.53 5.92 8.53H2.54C1.14 8.53 0 7.39 0 5.97V2.561C0 1.15 1.14 0 2.54 0ZM2.54 11.4697H5.92C7.33 11.4697 8.46 12.6107 8.46 14.0307V17.4397C8.46 18.8497 7.33 19.9997 5.92 19.9997H2.54C1.14 19.9997 0 18.8497 0 17.4397V14.0307C0 12.6107 1.14 11.4697 2.54 11.4697ZM17.4601 0H14.0801C12.6701 0 11.5401 1.15 11.5401 2.561V5.97C11.5401 7.39 12.6701 8.53 14.0801 8.53H17.4601C18.8601 8.53 20.0001 7.39 20.0001 5.97V2.561C20.0001 1.15 18.8601 0 17.4601 0ZM14.0801 11.4697H17.4601C18.8601 11.4697 20.0001 12.6107 20.0001 14.0307V17.4397C20.0001 18.8497 18.8601 19.9997 17.4601 19.9997H14.0801C12.6701 19.9997 11.5401 18.8497 11.5401 17.4397V14.0307C11.5401 12.6107 12.6701 11.4697 14.0801 11.4697Z" fill={color}/>
      </svg>,
      link: "/dashboard",
      customIcon: true,
      position: "top",
      notAllowed: ['']

    },
    {
      label: t("nav.analytics"),
      icon: ({ color }) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M5.33049 0.000427246H14.6695C18.0705 0.000427246 19.9905 1.92943 20.0005 5.33043V14.6704C20.0005 18.0704 18.0705 20.0004 14.6695 20.0004H5.33049C1.92949 20.0004 0.000488281 18.0704 0.000488281 14.6704V5.33043C0.000488281 1.92943 1.92949 0.000427246 5.33049 0.000427246ZM10.0495 15.8604C10.4805 15.8604 10.8395 15.5404 10.8795 15.1104V4.92043C10.9195 4.61043 10.7705 4.29943 10.5005 4.13043C10.2195 3.96043 9.87949 3.96043 9.61049 4.13043C9.33949 4.29943 9.19049 4.61043 9.21949 4.92043V15.1104C9.27049 15.5404 9.62949 15.8604 10.0495 15.8604ZM14.6505 15.8604C15.0705 15.8604 15.4295 15.5404 15.4805 15.1104V11.8304C15.5095 11.5094 15.3605 11.2104 15.0895 11.0404C14.8205 10.8704 14.4805 10.8704 14.2005 11.0404C13.9295 11.2104 13.7805 11.5094 13.8205 11.8304V15.1104C13.8605 15.5404 14.2195 15.8604 14.6505 15.8604ZM6.21949 15.1104C6.17949 15.5404 5.82049 15.8604 5.38949 15.8604C4.95949 15.8604 4.59949 15.5404 4.56049 15.1104V8.20043C4.53049 7.88943 4.67949 7.58043 4.95049 7.41043C5.21949 7.24043 5.56049 7.24043 5.83049 7.41043C6.09949 7.58043 6.25049 7.88943 6.21949 8.20043V15.1104Z" fill={color}/>
      </svg>,
      link: "/analytics",
      customIcon: true,
      position: "top",
      notAllowed: ['']

    },
    // {
    //   label: t("nav.team"),
    //   icon: "users",
    //   link: "/team",
    //  customIcon: false,
    //   position: "top",
    // },
    {
      label: t("nav.studio"),
      icon: ({ color }) => <svg width="18" height="24" viewBox="0 0 18 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9.54455 10.6003C6.86581 9.31523 3.56865 10.6201 2.17999 13.5148C0.141 17.7652 4.72195 19.4496 0.980732 23.2363C-1.10724 25.3497 9.61625 22.8881 11.8804 18.1683C13.269 15.2736 12.2232 11.8853 9.54455 10.6003Z" fill={color} />
      <path d="M17.0741 2.66607L11.5984 0.0392832C11.3891 -0.0610613 11.1599 0.0375593 11.0891 0.258519C11.0891 0.258519 9.89682 3.88026 9.48111 5.26903C9.37601 5.61988 9.27317 5.81361 9.59097 5.96597C10.5424 6.42222 12.6685 7.44261 13.7364 7.95473C14.0135 8.08765 14.1405 7.86206 14.3268 7.58076C15.0776 6.44689 17.2219 3.20061 17.2219 3.20061C17.3499 3.00694 17.2834 2.76642 17.0741 2.66607Z" fill={color}/>
      <path d="M13.1407 8.81008C12.1993 8.35865 10.2415 7.41899 9.30021 6.96756C8.92088 6.78565 8.85032 7.19048 8.79075 7.35211C8.65338 7.72466 8.3055 8.86424 8.3055 8.86424C8.23464 9.0852 8.36441 9.29343 8.59405 9.32702C8.59405 9.32702 9.47842 9.45632 10.0956 9.75236C10.8596 10.1188 11.6814 10.997 11.6814 10.997C11.8399 11.1664 12.0744 11.1467 12.2025 10.953C12.2025 10.953 12.9766 9.83778 13.1995 9.44449C13.2812 9.29997 13.4804 8.9729 13.1407 8.81008Z" fill={color}/>
      </svg>,
      link: "/studio",
      customIcon: true,
      position: "top",
      notAllowed: ['']

    },
    {
      label: t("nav.api_keys"),
      icon: ({ color }) => <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M19.6006 2.32787C20.1331 1.79534 20.1331 0.931939 19.6006 0.399402C19.0681 -0.133134 18.2046 -0.133134 17.6721 0.399402L9.79518 8.27627C8.80509 7.6411 7.62737 7.27273 6.36364 7.27273C2.8491 7.27273 0 10.1218 0 13.6364C0 17.1509 2.8491 20 6.36364 20C9.87818 20 12.7273 17.1509 12.7273 13.6364C12.7273 12.3726 12.3589 11.1949 11.7237 10.2048L14.2845 7.64398L15.4401 8.79955C16.1502 9.50964 17.3014 9.50964 18.0114 8.79955L20.2626 6.5483C20.9727 5.83826 20.9727 4.68705 20.2626 3.977L19.1071 2.8214L19.6006 2.32787ZM2.72727 13.6364C2.72727 11.6281 4.35533 10 6.36364 10C8.37194 10 10 11.6281 10 13.6364C10 15.6446 8.37194 17.2727 6.36364 17.2727C4.35533 17.2727 2.72727 15.6446 2.72727 13.6364Z" fill={color} />
      </svg>,
      link: "/keys",
      customIcon: true,
      position: "top",
      notAllowed: ['']

    },
    {
      label: t("nav.billing"),
      icon: ({ color }) => <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 4.54844C20 5.20844 19.46 5.74844 18.8 5.74844H1.2C0.54 5.74844 0 5.20844 0 4.54844V4.53844C0 2.24844 1.85 0.398438 4.14 0.398438H15.85C18.14 0.398438 20 2.25844 20 4.54844Z" fill={color}/>
      <path d="M0 8.45V13.46C0 15.75 1.85 17.6 4.14 17.6H15.85C18.14 17.6 20 15.74 20 13.45V8.45C20 7.79 19.46 7.25 18.8 7.25H1.2C0.54 7.25 0 7.79 0 8.45ZM6 14.25H4C3.59 14.25 3.25 13.91 3.25 13.5C3.25 13.09 3.59 12.75 4 12.75H6C6.41 12.75 6.75 13.09 6.75 13.5C6.75 13.91 6.41 14.25 6 14.25ZM12.5 14.25H8.5C8.09 14.25 7.75 13.91 7.75 13.5C7.75 13.09 8.09 12.75 8.5 12.75H12.5C12.91 12.75 13.25 13.09 13.25 13.5C13.25 13.91 12.91 14.25 12.5 14.25Z" fill={color}/>
      </svg>,
      link: "/billing",
      customIcon: true,
      position: "top",
      notAllowed: ['user', 'developer']

    },
    {
      label: t("nav.profile"),
      icon: ({ color }) => <svg width="16" height="22" viewBox="0 0 16 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M2.75 5.5C2.75 2.6005 5.1005 0.25 8 0.25C10.8995 0.25 13.25 2.6005 13.25 5.5C13.25 8.3995 10.8995 10.75 8 10.75C5.1005 10.75 2.75 8.3995 2.75 5.5Z" fill={color}/>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M0.25 17.5714C0.25 14.6325 2.63249 12.25 5.57143 12.25H10.4286C13.3675 12.25 15.75 14.6325 15.75 17.5714C15.75 19.8792 13.8792 21.75 11.5714 21.75H4.42857C2.12081 21.75 0.25 19.8792 0.25 17.5714Z" fill={color}/>
      </svg>,
      link: "/account",
      customIcon: true,
      position: "top",
      notAllowed: ['']

    },
    {
      label: t("nav.help"),
      icon: ({ color }) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.5816 16.5208C19.0889 14.77 20 12.4914 20 9.99994C20 7.50847 19.0889 5.22981 17.5816 3.479L13.3089 7.75172C13.745 8.39244 14 9.16644 14 9.99994C14 10.8334 13.745 11.6074 13.3089 12.2481L17.5816 16.5208Z" fill={color} />
      <path d="M16.5209 17.5817C14.7701 19.089 12.4915 20.0001 10 20.0001C7.50853 20.0001 5.22987 19.089 3.47906 17.5817L7.75178 13.309C8.3925 13.7451 9.1665 14.0001 10 14.0001C10.8335 14.0001 11.6075 13.7451 12.2482 13.309L16.5209 17.5817Z" fill={color}/>
      <path d="M2.4184 16.5208L6.69112 12.2481C6.25495 11.6074 6 10.8334 6 9.99994C6 9.16644 6.25495 8.39244 6.69112 7.75172L2.4184 3.479C0.91114 5.22981 0 7.50847 0 9.99994C0 12.4914 0.91114 14.77 2.4184 16.5208Z" fill={color}/>
      <path d="M10 6C9.1665 6 8.3925 6.25495 7.75178 6.69112L3.47906 2.4184C5.22987 0.91114 7.50853 0 10 0C12.4915 0 14.7701 0.91114 16.5209 2.4184L12.2482 6.69112C11.6075 6.25495 10.8335 6 10 6Z" fill={color}/>
      </svg>,
      link: "/help",
      customIcon: true,
      position: "top",
      notAllowed: ['']

    },
    {
      label: t("nav.signout"),
      icon: ({ color }) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M13.3337 14.1666L17.5003 9.99998L13.3337 5.83331" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M17.5 10H7.5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>,
      action: authContext.signout,
      customIcon: true,
      position: "bottom",
      notAllowed: ['']

    },
  ];
  return (
    <Fragment>
      <VerticalNav items={nav} />
      <DrawerNav items={nav} />

      <main className="relative z-10 px-0 pt-20 pb-4 min-h-screen sm:pl-[256px] sm:pr-0 sm:pt-0 fade-in">
        {/* Portal background with glassmorphism */}
        <div className="fixed inset-0 portal-gradient portal-dots-pattern">
          <div className="absolute inset-0 portal-gradient-overlay"></div>
          <div className="absolute inset-0 portal-mesh-pattern opacity-30"></div>
        </div>
        <div className="relative z-10">
          <Header title={t(title)}>
            <User />
          </Header>

          {children}
        </div>
      </main>
    </Fragment>
  );
}
